import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, TIME_PERIODS } from '../lib/utils';
import { 
  DollarSign, 
  TrendingDown, 
  Users, 
  FolderKanban, 
  Landmark,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import QuickEntryPanel from '../components/QuickEntryPanel';
import RoleWidgets from '../components/RoleWidgets';
import DocumentsWidget from '../components/DocumentsWidget';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { startOfDay, startOfWeek, startOfMonth, startOfYear, endOfDay, endOfWeek, endOfMonth, endOfYear, isWithinInterval, parseISO, format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#2E7D32', '#F57C00', '#8D6E63', '#1E88E5', '#E53935', '#43A047'];

const DashboardPage = () => {
  const { user, hasRole, hasPermission } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [allIncome, setAllIncome] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('monthly');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, finRes, incomeRes, expenseRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard`),
        axios.get(`${API_URL}/api/reports/financial-summary`),
        axios.get(`${API_URL}/api/income`),
        axios.get(`${API_URL}/api/expenses`),
      ]);
      setDashboardData(dashRes.data);
      setFinancialData(finRes.data);
      setAllIncome(incomeRes.data);
      setAllExpenses(expenseRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Filter data by time period
  const getFilteredData = (data, period) => {
    const now = new Date();
    let startDate, endDate;
    
    switch (period) {
      case 'daily':
        startDate = startOfDay(now);
        endDate = endOfDay(now);
        break;
      case 'weekly':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'monthly':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'yearly':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        break;
      default:
        return data;
    }
    
    return data.filter(item => {
      const itemDate = parseISO(item.date);
      return isWithinInterval(itemDate, { start: startDate, end: endDate });
    });
  };

  // Get period label
  const getPeriodLabel = (period) => {
    const now = new Date();
    switch (period) {
      case 'daily':
        return format(now, 'EEEE, MMMM d, yyyy');
      case 'weekly':
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'monthly':
        return format(now, 'MMMM yyyy');
      case 'yearly':
        return format(now, 'yyyy');
      default:
        return 'All Time';
    }
  };

  const periodLabel = getPeriodLabel(timePeriod);

  const filteredIncome = getFilteredData(allIncome, timePeriod);
  const filteredExpenses = getFilteredData(allExpenses, timePeriod);
  
  const periodIncome = filteredIncome.reduce((sum, i) => sum + i.amount, 0);
  const periodExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const periodNet = periodIncome - periodExpenses;

  // Group data for pie charts
  const incomeBySource = filteredIncome.reduce((acc, item) => {
    const source = item.source.replace('_', ' ');
    acc[source] = (acc[source] || 0) + item.amount;
    return acc;
  }, {});

  const expensesByCategory = filteredExpenses.reduce((acc, item) => {
    const cat = item.category.replace('_', ' ');
    acc[cat] = (acc[cat] || 0) + item.amount;
    return acc;
  }, {});

  const incomeChartData = Object.entries(incomeBySource).map(([name, value]) => ({ name, value }));
  const expenseChartData = Object.entries(expensesByCategory).map(([name, value]) => ({ name, value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stats = [
    {
      title: `${timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1)} Income`,
      value: formatCurrency(periodIncome),
      icon: DollarSign,
      perm: 'finance',
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      title: `${timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1)} Expenses`,
      value: formatCurrency(periodExpenses),
      icon: TrendingDown,
      perm: 'finance',
      color: 'text-red-600',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
    },
    {
      title: `${timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1)} Net`,
      value: formatCurrency(periodNet),
      icon: TrendingUp,
      perm: 'finance',
      color: periodNet >= 0 ? 'text-blue-600' : 'text-red-600',
      bgColor: periodNet >= 0 ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-red-50 dark:bg-red-900/20',
    },
    {
      title: 'Staff Members',
      value: dashboardData?.staff_count || 0,
      icon: Users,
      perm: 'personnel',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    },
    {
      title: 'Active Projects',
      value: dashboardData?.active_projects || 0,
      icon: FolderKanban,
      perm: 'projects',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    },
    {
      title: 'Active Loans',
      value: dashboardData?.active_loans || 0,
      icon: Landmark,
      perm: 'loans',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
    },
  ].filter((s) => hasPermission(s.perm, 'view'));

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground">
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h2>
          <p className="text-muted-foreground">
            Here's what's happening at Suubi today
          </p>
        </div>
        
        {/* Time Period Selector */}
        <div className="space-y-1">
          <Tabs value={timePeriod} onValueChange={setTimePeriod}>
            <TabsList data-testid="dashboard-period-tabs">
              {TIME_PERIODS.map((period) => (
                <TabsTrigger key={period.value} value={period.value} data-testid={`dashboard-${period.value}`}>
                  {period.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2 text-sm justify-end">
            <Calendar className="h-4 w-4 text-primary" />
            <span className="font-medium text-primary">{periodLabel}</span>
          </div>
        </div>
      </div>

      {/* All-Time Summary Banner (finance data — permission gated) */}
      {hasPermission('finance', 'view') && (
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-primary" />
              <span className="font-medium">All-Time Totals:</span>
            </div>
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="text-sm text-muted-foreground">Income:</span>
                <span className="ml-2 font-bold text-green-600">{formatCurrency(dashboardData?.total_income || 0)}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Expenses:</span>
                <span className="ml-2 font-bold text-red-600">{formatCurrency(dashboardData?.total_expenses || 0)}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Net Balance:</span>
                <span className="ml-2 font-bold text-primary">{formatCurrency(dashboardData?.net_balance || 0)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Role-Based Widgets (show role-specific insights at the top) */}
      <RoleWidgets user={user} />

      {/* Quick Data Entry Panel (visible to users with edit roles) */}
      <QuickEntryPanel user={user} hasRole={hasRole} onCreated={fetchData} />

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stats-grid">
        {stats.map((stat) => (
          <Card key={stat.title} className="card-hover" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                  <p className="text-2xl font-heading font-bold">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Documents widget — per-scope counts + expiring list (permission gated) */}
        {hasPermission('documents', 'view') && <DocumentsWidget />}

        {/* Income Distribution Pie Chart */}
        {hasPermission('finance', 'view') && (
        <Card className="card-hover" data-testid="income-chart">
          <CardHeader>
            <CardTitle className="font-heading capitalize">{timePeriod} Income Distribution</CardTitle>
            <CardDescription>Breakdown by source</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {incomeChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={incomeChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {incomeChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No income data for this {timePeriod} period
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Expense Categories Pie Chart */}
        {hasPermission('finance', 'view') && (
        <Card className="card-hover" data-testid="expense-chart">
          <CardHeader>
            <CardTitle className="font-heading capitalize">{timePeriod} Expense Categories</CardTitle>
            <CardDescription>Where money is spent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {expenseChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={expenseChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {expenseChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No expense data for this {timePeriod} period
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Monthly Trends */}
      {hasPermission('finance', 'view') && (
      <Card className="card-hover" data-testid="trends-chart">
        <CardHeader>
          <CardTitle className="font-heading">Financial Trends</CardTitle>
          <CardDescription>Income vs Expenses over time (All months)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={financialData?.monthly_trends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
                <Legend />
                <Bar dataKey="income" name="Income" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#E53935" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Recent Transactions */}
      {hasPermission('finance', 'view') && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Income */}
        <Card className="card-hover" data-testid="recent-income">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Recent Income</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.recent_income || []).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">{item.description || item.source.replace('_', ' ')}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(item.date)}</p>
                  </div>
                  <Badge className="badge-success">{formatCurrency(item.amount)}</Badge>
                </div>
              ))}
              {(!dashboardData?.recent_income || dashboardData.recent_income.length === 0) && (
                <p className="text-muted-foreground text-center py-4">No recent income</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Expenses */}
        <Card className="card-hover" data-testid="recent-expenses">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Recent Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.recent_expenses || []).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">{item.description || item.category.replace('_', ' ')}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(item.date)}</p>
                  </div>
                  <Badge className="badge-danger">{formatCurrency(item.amount)}</Badge>
                </div>
              ))}
              {(!dashboardData?.recent_expenses || dashboardData.recent_expenses.length === 0) && (
                <p className="text-muted-foreground text-center py-4">No recent expenses</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
};

export default DashboardPage;
